import type {
  ChangedFieldPathToken,
  ChangeTrackingSource,
} from '@typeDefinitions/changeTrackingTypes';
import type { CarrierDetails } from '@store/carriers';

import { CHANGE_TRACKING_SOURCES } from '../../utils/changeTrackingRegistry';
import { createFormChangeTracker } from '../../utils/createFormChangeTracker';
import { findChangedForms } from '../../utils/findChangedForms';

const IGNORED_FIELDS = new Set([
  'averageRating',
  'comments',
  'createdDate',
  'documentPath',
  'documents',
  'falloutCount',
  'hasExpiredCargoInsurance',
  'hasExpiredLiabilityInsurance',
  'hasIdentityAlerts',
  'hasWatchDogAlerts',
  'highwayMetadataViewModel',
  'highwayUrl',
  'isDuplicate',
  'isValid',
  'modifiedDate',
  'numRatings',
  'numberOfLoads',
  'operationType',
  'safetyWarnings',
  'showDuplicateBanner',
]);

const DETAILS_FIELDS = new Set([
  'allowAdvances',
  'alreadySetup',
  'blacklist',
  'brokerAuthority',
  'commonAuthority',
  'conditionalActionPlanReceived',
  'contractAuthority',
  'corporatePhoneNumber',
  'currentContactId',
  'dot',
  'isFavorite',
  'lessThanThreeLoads',
  'mc',
  'mode',
  'name',
  'needs1099',
  'nonUs',
  'notes',
  'onboardingVendorCarrierExclusion',
  'operatingService',
  'overallMaxLiabilityClass',
  'overallRiskAssessment',
  'requireMacropointTracking',
  'safetyRating',
  'shipperVerifyMCDotNumber',
  'status',
  'vendorAssessment',
  'vendorHold',
  'vendorHoldDescription',
  'vendorHoldReason',
]);

const LIABILITY_INSURANCE_FIELDS = new Set([
  'liabilityExpiration',
  'liabilityInsuranceCompany',
  'liabilityLimit',
  'liabilityPolicyNumber',
]);

const CARGO_INSURANCE_FIELDS = new Set([
  'cargoExpiration',
  'cargoInsuranceCompany',
  'cargoLimit',
  'cargoPolicyNumber',
  'hazmatCertified',
  'highValueCertified',
  'reeferInsurance',
]);

const WORKERS_COMP_FIELDS = new Set([
  'workersCompExpiration',
  'workersCompInsuranceCompany',
  'workersCompLimitDisease',
  'workersCompLimitEachAccident',
  'workersCompLimitPolicy',
  'workersCompPolicyNumber',
]);

const ASSESSMENT_FIELDS = new Set([
  'caRefrigeratedFullyPassed',
  'canadianCrossBorderFullyPassed',
  'coBrokerFullyPassed',
  'hazmatFullyPassed',
  'highValueCargoFullyPassed',
  'interstateFullyPassed',
  'intrastateFullyPassed',
  'refrigeratedFullyPassed',
]);

const BILLING_FIELDS = new Set([
  'accountingSysId',
  'billingContactEmail',
  'billingTerms',
  'emailCopy',
  'factor',
  'paymentTermDays',
  'remitTo',
  'taxId',
  'taxIDVerificationDate',
]);

const ADDRESS_BILLING_FIELDS = new Set(['achAccount', 'achRouting', 'enabled']);

const ADDRESS_FACTORING_COMPANY_FIELDS = new Set([
  'factoringCompany',
  'factoringCompanyId',
]);

const LTL_SETTINGS_FIELDS = new Set([
  'ltlEnabled',
  'ltlRateNote',
  'ltlTerminalLink',
  'transfloEnabled',
]);

const isEmptyFormValue = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

const getPropertyPath = (path: readonly ChangedFieldPathToken[]): string[] =>
  path.flatMap((token) => (token.type === 'property' ? [token.key] : []));

const areDatesEqual = (before: string, after: string): boolean => {
  const beforeDate = new Date(before);
  const afterDate = new Date(after);

  return (
    !Number.isNaN(beforeDate.getTime()) &&
    !Number.isNaN(afterDate.getTime()) &&
    beforeDate.toDateString() === afterDate.toDateString()
  );
};

const areCarrierScalarValuesEqual = (
  before: unknown,
  after: unknown,
  path: readonly ChangedFieldPathToken[],
): boolean => {
  if (Object.is(before, after)) return true;
  if (isEmptyFormValue(before) && isEmptyFormValue(after)) return true;

  if (typeof before === 'number' && typeof after === 'string') {
    return after.trim() !== '' && Number(after) === before;
  }

  if (typeof before === 'string' && typeof after === 'number') {
    return before.trim() !== '' && Number(before) === after;
  }

  if (typeof before === 'string' && typeof after === 'string') {
    const field = getPropertyPath(path).at(-1) ?? '';

    if (/date|expiration/i.test(field) && areDatesEqual(before, after)) {
      return true;
    }

    if (/phone/i.test(field)) {
      return before.replace(/\D/g, '') === after.replace(/\D/g, '');
    }
  }

  return false;
};

const getCarrierDetailsForm = (
  path: readonly ChangedFieldPathToken[],
): ChangeTrackingSource => {
  const [field, nestedField = ''] = getPropertyPath(path);

  if (field === 'canAddress' || field === 'usaAddress') {
    if (ADDRESS_FACTORING_COMPANY_FIELDS.has(nestedField)) {
      return CHANGE_TRACKING_SOURCES.carrierDetails
        .carrierDetailsFactoringCompany;
    }

    if (ADDRESS_BILLING_FIELDS.has(nestedField)) {
      return CHANGE_TRACKING_SOURCES.carrierDetails.carrierDetailsBilling;
    }

    if (nestedField.startsWith('mailing')) {
      return CHANGE_TRACKING_SOURCES.carrierDetails
        .carrierDetailsMailingAddress;
    }

    return CHANGE_TRACKING_SOURCES.carrierDetails.carrierDetailsPhysicalAddress;
  }

  if (field === 'carrierHighwayRuleAssociations') {
    return CHANGE_TRACKING_SOURCES.carrierDetails
      .carrierDetailsHighwayIntegrationData;
  }

  if (field === 'carrierInsurances') {
    return CHANGE_TRACKING_SOURCES.carrierDetails.carrierDetailsInsurance;
  }

  if (field === 'certifications') {
    return CHANGE_TRACKING_SOURCES.carrierDetails.carrierDetailsCertifications;
  }

  if (field === 'contacts') {
    return CHANGE_TRACKING_SOURCES.carrierDetails.carrierDetailsContacts;
  }

  if (field === 'equipments') {
    return CHANGE_TRACKING_SOURCES.carrierDetails.carrierDetailsEquipment;
  }

  if (field === 'lanes') {
    return CHANGE_TRACKING_SOURCES.carrierDetails.carrierDetailsDetails;
  }

  if (field === 'maxLiabilities') {
    return CHANGE_TRACKING_SOURCES.carrierDetails.carrierDetailsMaxLtlLiability;
  }

  if (field === 'ratings' || field === 'officeRating' || field === 'rating') {
    return CHANGE_TRACKING_SOURCES.carrierDetails.carrierDetailsRatings;
  }

  if (ASSESSMENT_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.carrierDetails.carrierDetailsAssessments;
  }

  if (BILLING_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.carrierDetails.carrierDetailsBilling;
  }

  if (CARGO_INSURANCE_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.carrierDetails.carrierDetailsCargoInsurance;
  }

  if (DETAILS_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.carrierDetails.carrierDetailsDetails;
  }

  if (LIABILITY_INSURANCE_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.carrierDetails
      .carrierDetailsLiabilityInsurance;
  }

  if (LTL_SETTINGS_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.carrierDetails.carrierDetailsLtlSettings;
  }

  if (WORKERS_COMP_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.carrierDetails.carrierDetailsWorkersComp;
  }

  return CHANGE_TRACKING_SOURCES.carrierDetails.carrierDetailsDetails;
};

const findCarrierDetailsChangedForms = (
  before: CarrierDetails,
  after: CarrierDetails,
): ChangeTrackingSource[] =>
  findChangedForms(before, after, {
    ignoredFields: IGNORED_FIELDS,
    areScalarValuesEqual: areCarrierScalarValuesEqual,
    getForm: (change) => getCarrierDetailsForm(change.path),
  });

const carrierDetailsFormChangeTracker = createFormChangeTracker<
  CarrierDetails,
  ChangeTrackingSource
>({
  getIdentity: (carrier) => carrier.id || undefined,
  findChangedForms: findCarrierDetailsChangedForms,
});

export const setCarrierDetailsChangeBaseline =
  carrierDetailsFormChangeTracker.setBaseline;
export const synchronizeCarrierDetailsChangeHydration =
  carrierDetailsFormChangeTracker.synchronizeHydration;
export const prepareCarrierDetailsChangeSummary =
  carrierDetailsFormChangeTracker.prepare;
